import { isNil, mapValues } from "lodash";
import { isEmpty } from "underscore";
import { AssignedPedestrianProductInfo, GetDelegatedPedestrianRightsQuery, InvitePedestrianRightResponse, UpdatePedestrianRightDelegeeModel, UpdatePedestrianRightResponse } from "../../api/pedestrianRights.api";
import { Statuses } from "../../components/DelegeeStatus/DelegeeStatus";
import { DelegatedPedestrianRight } from "../../models/delegations/DelegatedPedestrianRight";
import { InvitePedestrianRightRequestModel } from "../../models/InvitePedestrianRightRequestModel";
import { PedestrianProductModel } from "../../models/PedestrianProductModel";
import { DeletePedestrianDelegeesModel } from "../../models/vehicle-assignments/DeletePedestrianDelegeesModel";
import { RevokePedestrianRightsModel } from "../../models/vehicle-assignments/RevokePedestrianRightsModel";
import { PedestrianRightsPagedData, PedestrianRightsState } from "../reducers/pedestrian-rights.reducer";
import { DelegatedParkingProductModel, DelegeeWithProductsModel } from "../../models/delegations/DelegatedParkingProductsModel";
import moment from "moment";

export function reduceRevokePedestrianMyProducts(
    state: PedestrianRightsPagedData<DelegatedPedestrianRight>,
    revokeModel: RevokePedestrianRightsModel
): PedestrianRightsPagedData<DelegatedPedestrianRight> {
    const newPages = mapValues(state.pages, (page) => {
        return { ...page, data: page.data?.filter((item) => !revokeModel.delegeeRevokeModels.flatMap(d => d.products)
            .map(p => p.entryRightId)
            .includes(item.id)) };
    });

    return {
        ...state,
        pages: newPages,
        totalRecords: state.totalRecords - revokeModel.delegeeRevokeModels.flatMap(d => d.products).length,
    };
}

export function reduceDeletePedestrianMyProducts(
    state: PedestrianRightsPagedData<DelegatedPedestrianRight>,
    deleteModel: DeletePedestrianDelegeesModel
): PedestrianRightsPagedData<DelegatedPedestrianRight> {
    let rowsFiltered = 0;
    const newPages = mapValues(state.pages, (page) => {
        return { ...page, data: page.data?.filter((item) => {
            const shouldRemove = deleteModel.delegeeIds.includes(item.delegee.id);
            if(shouldRemove) {
                rowsFiltered++;
                return false;
            }
            return true;
        })};
    });

    return {
        ...state,
        pages: newPages,
        totalRecords: state.totalRecords - rowsFiltered,
    };
}

export function reduceInvitePedestrianMyProducts(
    state: PedestrianRightsPagedData<DelegatedPedestrianRight>,
    pedestrianProducts: PedestrianProductModel[],
    request: InvitePedestrianRightRequestModel,
    response: InvitePedestrianRightResponse): PedestrianRightsPagedData<DelegatedPedestrianRight> {
    const fakeDelegees = response.assignedPedestrianProducts.map((pedestrianProduct): DelegatedPedestrianRight => {
        const product = pedestrianProducts.find(product => product.pmcId === pedestrianProduct.pmcId && product.seasonTicketReferenceId === pedestrianProduct.contractId);

        return {
            delegee: {
                email: request.email,
                name: request.name ?? "",
                id: response.delegeeId,
            },
            id: pedestrianProduct.entryRightId,
            invitationDate: new Date(),
            product: {
                pmcId: pedestrianProduct.pmcId,
                locationName: product?.locationName ?? "",
                pmcName: product?.pmcName ?? "",
                placeId: 42,
            },
            status: Statuses.Pending
        }
    }
    );

    const newPages = mapValues(state.pages, (page) => {
        return { ...page, data: fakeDelegees.concat(page?.data ?? []) }
    });

    return {
        ...state,
        pages: newPages,
        totalRecords: state.totalRecords + fakeDelegees.length,
    };
}

// please call it with immer draft
export function applyPedestrianUpdateOnDraftMyProducts(
    state: PedestrianRightsState,
    request: UpdatePedestrianRightDelegeeModel,
    response: UpdatePedestrianRightResponse
) {
    const originalDelegee = state.delegeeDetails.data;
    if (!originalDelegee) {
        throw new Error("Need original delegee to add new rows");
    }
    const entriesToAdd = response.assignedPedestrianProducts
        .filter((aPp) => !originalDelegee.pmcIds.includes(aPp.pmcId))
        .map((aPp): DelegatedPedestrianRight => {
            const product = state.pedestrianProducts.data!.find((x) => x.pmcId === aPp.pmcId);
            return {
                delegee: {
                    email: originalDelegee.delegeeEmail,
                    name: request.name,
                    id: originalDelegee.id,
                },
                id: aPp.entryRightId,
                invitationDate: new Date(),
                product: {
                    pmcId: aPp.pmcId,
                    locationName: product?.locationName ?? "",
                    pmcName: product?.pmcName ?? "",
                    placeId: 42,
                },
                status: originalDelegee.status,
            };
        });

    const removeCounter = originalDelegee.pmcIds.filter((x) => !request.pmcIds.includes(x)).length;

    for (const pageNumber in state.myProducts.pages) {
        const page = state.myProducts.pages[pageNumber];
        if (!page.data) {
            continue;
        }
        for (const product of page.data) {
            if (product.delegee.id === request.delegeeId) {
                product.delegee.name = request.name;
            }
        }
        const entriesToDelete = page.data.filter(
            (x) => x.delegee.id === request.delegeeId && !request.pmcIds.includes(x.product.pmcId)
        );
        const matchingQueryEntries = entriesToAdd.filter((x) => pedestrianRightMatches(x, page.query));
        page.data = matchingQueryEntries.concat(page.data.filter((x) => !entriesToDelete.includes(x)));
    }
    state.myProducts.totalRecords = state.myProducts.totalRecords - removeCounter + entriesToAdd.length;
}

export function applyOptimisticUpdateOnMyFleet(state: PedestrianRightsState, request: UpdatePedestrianRightDelegeeModel, response: UpdatePedestrianRightResponse) {
    const createProduct = (x: AssignedPedestrianProductInfo): DelegatedParkingProductModel => {
        const product = state.pedestrianProducts.data?.find((p) => p.pmcId === x.pmcId)!;
        return {
            pmcId: x.pmcId,
            parkingRightId: x.entryRightId,
            id: x.pmcId,
            name: product.pmcName,
            location: product.locationName,
            created: new Date(),
            status: Statuses.Pending,
        };
    }

    for (const page in state.myFleet.delegees.pages) {
        const currentPage = state.myFleet.delegees.pages[page as any as number];
        const delegee = currentPage.data!.find((d) => d.registrationId === request.delegeeId);

        if (!delegee) {
            continue;
        }

        delegee.name = request.name;
        const newProducts = response.assignedPedestrianProducts.filter(x => !delegee.products.some(p => p.pmcId === x.pmcId)).map(createProduct);
        const removedProducts = delegee.products.filter(p => !response.assignedPedestrianProducts.some(x => x.pmcId === p.pmcId));
        delegee.products = delegee.products.filter(p => !removedProducts.includes(p)).concat(newProducts);
    }
}

export function applyOptimisticInviteOnMyFleet(state: PedestrianRightsState, request: InvitePedestrianRightRequestModel, response: InvitePedestrianRightResponse) {
    const newDelegee : DelegeeWithProductsModel = {
        registrationId: response.delegeeId,
        name: request.name ?? "",
        emailAddress: request.email,
        invitationAccepted: false,
        invitationDate: moment().format("DD/MM/YYYY"),
        products: response.assignedPedestrianProducts.map((x): DelegatedParkingProductModel => {
            const product = state.pedestrianProducts.data?.find((p) => p.pmcId === x.pmcId)!;
            return {
                pmcId: x.pmcId,
                parkingRightId: x.entryRightId,
                id: x.pmcId,
                name: product.pmcName,
                location: product.locationName,
                created: new Date(),
                status: Statuses.Pending,
            };
        }),
    }

    for (const page in state.myFleet.delegees.pages) {
        const currentPage = state.myFleet.delegees.pages[page as any as number];
        currentPage.data!.unshift(newDelegee);
    }

    state.myFleet.delegees.totalRecords += 1;
}

export function applyOptimisticDeleteMyFleet(state: PedestrianRightsState, deleteModel: DeletePedestrianDelegeesModel) {
    for (const page in state.myFleet.delegees.pages) {
        const currentPage = state.myFleet.delegees.pages[page as any as number];
        currentPage.data = currentPage.data?.filter((d) => !deleteModel.delegeeIds.includes(d.registrationId));
    }
    state.myFleet.delegees.totalRecords -= deleteModel.delegeeIds.length;
}

export function applyOptimisticRevokeOnMyFleet(state: PedestrianRightsState, revokeModel: RevokePedestrianRightsModel) {
    for (const page in state.myFleet.delegees.pages) {
        const currentPage = state.myFleet.delegees.pages[page as any as number];
        for (const delegeeRevoke of revokeModel.delegeeRevokeModels) {
            const delegee = currentPage.data?.find(d => d.registrationId === delegeeRevoke.delegeeId);
            if (delegee) {
                delegee.products = delegee.products.filter(
                    p => !delegeeRevoke.products.map(p => p.entryRightId).includes(p.parkingRightId)
                );
            }
        }
    }
}

function pedestrianRightMatches(x: DelegatedPedestrianRight, query: GetDelegatedPedestrianRightsQuery): boolean {
    return (
        (isNil(query.pmcIds) || isEmpty(query.pmcIds) || query.pmcIds.includes(x.product.pmcId)) &&
        (isNil(query.email) || isEmpty(query.email) || x.delegee.email.includes(query.email)) &&
        (isNil(query.name) || isEmpty(query.name) || x.delegee.name.includes(query.name)) &&
        (isNil(query.statuses) || isEmpty(query.statuses) || query.statuses.includes(x.status)) &&
        (isNil(query.invitationFrom) || x.invitationDate.getTime() >= new Date(query.invitationFrom).getTime()) &&
        (isNil(query.invitationTo) || x.invitationDate.getTime() <= new Date(query.invitationTo).getTime())
    );
}
